/**
 * ViewToggle — the shared List View / Detail View segmented switch
 * (BO UI Standard, Settings-BO-Method-Example.html `.toggle`).
 *
 * Shown on CRUD-enabled Settings BOs: "List View" is the landing view
 * (standing rule 10); "Detail View" opens the selected record (or the first
 * row) in the shared full-page SettingsDetailPage.  Styled EXACTLY to the
 * prototype: bordered 8px pill pair, active segment = theme orange.
 */
interface Props {
  view: 'list' | 'detail';
  onChange: (view: 'list' | 'detail') => void;
  /** Disable the Detail segment (e.g. no rows to show). */
  detailDisabled?: boolean;
}

export function ViewToggle({ view, onChange, detailDisabled }: Props) {
  return (
    <span className="view-toggle" role="tablist" aria-label="List or Detail view">
      <button
        type="button" role="tab" aria-selected={view === 'list'}
        className={view === 'list' ? 'on' : ''}
        onClick={() => view !== 'list' && onChange('list')}
      >
        List View
      </button>
      <button
        type="button" role="tab" aria-selected={view === 'detail'}
        className={view === 'detail' ? 'on' : ''}
        disabled={detailDisabled}
        onClick={() => view !== 'detail' && onChange('detail')}
      >
        Detail View
      </button>
      <style>{`
        /* EXACT prototype .toggle (docs/prototypes/Settings-BO-Method-Example.html) */
        .view-toggle { display: inline-flex; border: 1px solid var(--ai-line); border-radius: 8px; overflow: hidden; background: #fff; }
        .view-toggle button { border: 0; background: #fff; padding: 7px 14px; font-size: 12.5px; font-weight: 700; color: #55607a; cursor: pointer; font-family: inherit; }
        .view-toggle button.on { background: var(--brand-accent, var(--ai-orange)); color: #fff; }
        .view-toggle button:disabled { opacity: .45; cursor: default; }
      `}</style>
    </span>
  );
}
